import { format } from 'date-fns'
import {
  Clock,
  ExternalLink,
  KeyRound,
  Loader2,
  Monitor,
  ShieldCheck,
  Terminal
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ACCESS_METHODS,
  AWS_ACCESS_REQUEST_STATUS,
  AwsAccessMethod,
  AwsAccessRequest,
  IS_MOCK_BROKER,
  getAwsAccessBrokerClient,
  mockApproveRequest,
  mockSeedApprovedRequest
} from '@/api/aws-access-broker-client'
import { RequestAwsAccessDialog } from '@/components/request-aws-access-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/use-toast'
import { useAuth } from '@/providers/AuthContextProvider'

const principles = [
  {
    title: 'Deliberate & isolated',
    description:
      'Production access is granted to an isolated, audited session — never to your local workstation, terminal, IDE, scripts, or AI coding agents.'
  },
  {
    title: 'Second-factor approval',
    description:
      'You confirm every request from Slack, an out-of-band step an agent driving your tools cannot complete. Approving your own request is fine. The Slack thread is the auditable record.'
  },
  {
    title: 'Attributed & time-limited',
    description:
      'Sessions are named after you and your approver, expire automatically, and every action is logged. CLI jump hosts are destroyed when the session ends.'
  },
  {
    title: 'Least privilege',
    description:
      'Request the lowest access level that gets the job done. Administrator access should be the rare exception, not the default.'
  }
]

const LEVEL_LABEL: Record<string, string> = {
  READ_ONLY: 'Read-only',
  DEVELOPER: 'Developer',
  ADMIN: 'Administrator'
}

const formatTime = (iso?: string) => {
  if (!iso) return '—'

  try {
    return format(new Date(iso), 'dd MMM yyyy, HH:mm')
  } catch {
    return iso
  }
}

export default function AwsAccessPage() {
  const { userData } = useAuth()
  const requestedBy = userData?.attributes?.email ?? 'unknown@epilot.cloud'

  const [dialogOpen, setDialogOpen] = useState(false)
  const [request, setRequest] = useState<AwsAccessRequest | null>(null)
  const [activating, setActivating] = useState(false)
  const requestIdRef = useRef<string | null>(null)

  const refreshCurrent = useCallback(async () => {
    if (!requestIdRef.current) return

    const latest = await getAwsAccessBrokerClient().getRequest(
      requestIdRef.current
    )

    if (latest) setRequest(latest)
  }, [])

  const onRequested = useCallback(
    async (requestId: string) => {
      requestIdRef.current = requestId
      await refreshCurrent()
    },
    [refreshCurrent]
  )

  // Poll while PENDING — the real broker flips status when Slack approves.
  useEffect(() => {
    if (!request || request.status !== AWS_ACCESS_REQUEST_STATUS.PENDING) return

    const interval = setInterval(() => void refreshCurrent(), 2000)

    return () => clearInterval(interval)
  }, [request, refreshCurrent])

  const handleSimulateApproval = () => {
    if (!request) return

    mockApproveRequest(request.id)
    void refreshCurrent()
  }

  const handlePreviewApproved = () => {
    const seeded = mockSeedApprovedRequest(requestedBy)

    requestIdRef.current = seeded.id
    setRequest(seeded)
  }

  const handleActivate = async (method: AwsAccessMethod) => {
    if (!request) return

    setActivating(true)

    try {
      const activated = await getAwsAccessBrokerClient().activateRequest(
        request.id,
        method
      )

      setRequest(activated)
    } catch (error) {
      toast({
        title: 'Activation failed',
        description:
          error instanceof Error
            ? error.message
            : 'Could not mint session. Try again.',
        variant: 'destructive'
      })
    } finally {
      setActivating(false)
    }
  }

  const isApproved = request?.status === AWS_ACCESS_REQUEST_STATUS.APPROVED
  const isActivated =
    isApproved && (!!request?.console_url || !!request?.ssm_command)

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-primary flex items-center gap-2">
          <KeyRound className="text-primary" size={20} />
          Production AWS access
        </h1>
        <p className="text-sm text-muted-foreground">
          Access to the production AWS account (
          <span className="font-medium">epilot-production</span>) is deliberate
          and isolated. Instead of long-lived credentials on your machine,
          reachable by terminals, scripts, and autonomous AI agents, the broker
          mints a short-lived, individually-attributed session once you confirm
          your request from Slack.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {principles.map((item) => (
          <div className="rounded-md border border-border p-4" key={item.title}>
            <p className="font-semibold text-primary text-sm">{item.title}</p>
            <p className="text-sm text-muted-foreground mt-1 leading-snug">
              {item.description}
            </p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={() => setDialogOpen(true)} variant="destructive">
          Request production access
        </Button>
        {IS_MOCK_BROKER && (
          <Button onClick={handlePreviewApproved} variant="outline">
            Preview approved request (dev)
          </Button>
        )}
      </div>

      {request && (
        <div className="rounded-md border border-border">
          <div className="flex items-center justify-between gap-4 border-b border-border p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-primary" size={18} />
              <h2 className="text-base font-semibold text-primary">
                Current request
              </h2>
            </div>
            <Badge variant={isApproved ? 'default' : 'secondary'}>
              {request.status}
            </Badge>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {LEVEL_LABEL[request.access_level]}
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock size={12} />
                {request.duration_minutes} min · requested{' '}
                {formatTime(request.created_at)}
                {request.approved_by &&
                  ` · ${
                    request.status === AWS_ACCESS_REQUEST_STATUS.DENIED
                      ? 'denied'
                      : 'approved'
                  } by ${request.approved_by}`}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{request.reason}</p>

            {request.status === AWS_ACCESS_REQUEST_STATUS.PENDING && (
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-primary flex items-center gap-2">
                  <Loader2 className="animate-spin" size={14} />
                  Waiting for approval in #dev-security…
                </p>
                {IS_MOCK_BROKER && (
                  <Button
                    onClick={handleSimulateApproval}
                    size="sm"
                    variant="outline"
                  >
                    Simulate approval (dev)
                  </Button>
                )}
              </div>
            )}

            {/* Approved — ask how the user wants to connect */}
            {isApproved && !isActivated && !activating && (
              <div className="rounded-md bg-accent p-3 space-y-3">
                <p className="text-sm font-medium text-primary">
                  Your request was approved. How do you want to connect?
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 rounded-md border border-border bg-background p-3 space-y-2">
                    <p className="text-xs font-semibold text-primary flex items-center gap-1">
                      <Monitor size={12} />
                      AWS Console
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Opens a browser session in the AWS Console.
                    </p>
                    <Button
                      className="w-full"
                      onClick={() =>
                        void handleActivate(ACCESS_METHODS.CONSOLE)
                      }
                      size="sm"
                      variant="default"
                    >
                      <ExternalLink className="mr-2" size={14} />
                      Open in Console
                    </Button>
                  </div>
                  <div className="flex-1 rounded-md border border-border bg-background p-3 space-y-2">
                    <p className="text-xs font-semibold text-primary flex items-center gap-1">
                      <Terminal size={12} />
                      CLI jump host
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Launches a jump host with prod credentials. Ready in ~90s.
                    </p>
                    <Button
                      className="w-full"
                      onClick={() => void handleActivate(ACCESS_METHODS.CLI)}
                      size="sm"
                      variant="outline"
                    >
                      <Terminal className="mr-2" size={14} />
                      Connect via CLI
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Minting the session */}
            {activating && (
              <p className="text-sm text-primary flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} />
                Minting your session…
              </p>
            )}

            {/* Session ready */}
            {isActivated && (
              <div className="rounded-md bg-accent p-3 space-y-3">
                {request.console_url && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-primary flex items-center gap-1">
                      <ExternalLink size={12} />
                      Open the AWS Console:
                    </p>
                    <Button
                      onClick={() =>
                        window.open(
                          request.console_url,
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                      size="sm"
                      variant="default"
                    >
                      <ExternalLink className="mr-2" size={14} />
                      Open AWS Console
                    </Button>
                  </div>
                )}

                {request.ssm_command && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-primary flex items-center gap-1">
                      <Terminal size={12} />
                      Connect via CLI (jump host ready in ~90s):
                    </p>
                    <code className="block rounded bg-background p-2 text-xs text-primary break-all">
                      {request.ssm_command}
                    </code>
                  </div>
                )}

                {request.expires_at && (
                  <p className="text-xs text-muted-foreground">
                    Session expires {formatTime(request.expires_at)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <RequestAwsAccessDialog
        onOpenChange={setDialogOpen}
        onRequested={onRequested}
        open={dialogOpen}
        requestedBy={requestedBy}
      />
    </div>
  )
}
